import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { useQrCodes, QrCodeUrl } from "./hooks/useQrCodes";
import { SearchHeader } from "./components/SearchHeader";
import { AssignEvseDialog } from "./components/dialogs/AssignEvseDialog";
import { PreviewQrDialog } from "./components/dialogs/PreviewQrDialog";
import { GenerateMoreDialog } from "./components/dialogs/GenerateMoreDialog";
import { ActionsCell } from "./components/ActionCell";

import "./QrCodeEvseManagement.css"

type ModalState =
  | { name: null, url?: never }
  | { name: "assign"; url: string }
  | { name: "preview"; url: string }
  | { name: "generate", url?: never };

const QrCodeEvseManagement: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { qrcodes, initialLoading, refreshing, assignTargetId, unassignQrCode, generateMore } = useQrCodes(toast);

  const [search, setSearch] = useState<string>("");
  const [modal, setModal] = useState<ModalState>({ name: null });

  const filters = useMemo(
    () => ({
      global: { value: search, matchMode: FilterMatchMode.CONTAINS },
    }),
    [search]
  );

  const header = useMemo(() => <SearchHeader value={search} onChange={setSearch} placeholder="Search by Target ID or URL" />, [search]);

  const onAssign = useCallback((url: string) => setModal({ name: "assign", url }), []);
  const onPreview = useCallback((url: string) => setModal({ name: "preview", url }), []);
  const onGenerateMore = useCallback(() => setModal({ name: "generate" }), []);
  const closeModal = useCallback(() => setModal({ name: null }), []);

  const onUnassign = useCallback((url: string) => {
    confirmDialog({
      message: "Are you sure you want to unassign this QR code from its current target?",
      header: "Confirm Unassign",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => unassignQrCode(url),
    });
  }, [unassignQrCode]);

  const targetIdBodyTemplate = (rowData: QrCodeUrl) => {
    if (!rowData.target) return null;
    return (
      <div className="flex align-items-center gap-2">
        <span>{rowData.target}</span>
        {rowData.isLocation !== null && (
          <Tag
            value={rowData.isLocation ? "Location" : "EVSE"}
            severity={rowData.isLocation ? "info" : "success"}
          />
        )}
      </div>
    );
  };

  const renderModal = () => {
    switch (modal.name) {
      case "assign":
        return (
          <AssignEvseDialog
            visible
            url={modal.url}
            onHide={closeModal}
            onAssign={async (targetId, isLocation) => {
              if (modal.url && targetId) {
                try {
                  await assignTargetId(modal.url, targetId, isLocation);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        );
      case "preview":
        return <PreviewQrDialog visible url={modal.url} onHide={closeModal} />;
      case "generate":
        return (
          <GenerateMoreDialog
            visible
            onHide={closeModal}
            onGenerate={async (qty) => {
              try {
                await generateMore(qty);
                closeModal();
              } catch {
                // keep modal open on error
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" appendTo={typeof window !== "undefined" ? document.body : undefined} />
      <ConfirmDialog />
      {initialLoading ? (
        <div className="loading-container">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <Card
            className="p-4"
            title={
              <div className="flex flex-column md:flex-row justify-content-between align-items-center">
                <div className="text-2xl font-semibold text-900">QR Code Management</div>
                <Button label="Generate More Urls" icon="pi pi-plus" onClick={onGenerateMore} />
              </div>
            }
          >
            <DataTable
              value={qrcodes}
              paginator
              rows={10}
              filters={filters}
              dataKey="qrCodeUrl"
              globalFilterFields={["qrCodeUrl", "evseId"]}
              emptyMessage="No QR codes found."
              header={header}
              loading={refreshing}
              className="p-datatable-gridlines"
            >
              <Column field="qrCodeUrl" header="QR Code Url" sortable />
              <Column field="target" header="Target ID" body={targetIdBodyTemplate} style={{ minWidth: "20rem" }} sortable />
              <Column
                header="Actions"
                body={(row: QrCodeUrl) => (
                  <ActionsCell row={row} onAssign={onAssign} onPreview={onPreview} onUnassign={onUnassign} />
                )}
                style={{ minWidth: "12rem" }}
              />
            </DataTable>
          </Card>

          {renderModal()}
        </>
      )}
    </>
  );
};

export default QrCodeEvseManagement;